import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Separator } from '../components/ui/separator';

const OrderDetailPage = () => {
  const { orderId, orderNumber } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Функция для перевода статусов на русский
  const getStatusLabel = (status) => {
    const statusMap = {
      'pending': 'В ожидании',
      'processing': 'В обработке',
      'completed': 'Завершен',
      'cancelled': 'Отменен'
    };
    return statusMap[status] || status;
  };

  useEffect(() => {
    // Запрет индексации
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, nofollow';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, []);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        setLoading(true);
        let orderData;
        if (orderNumber) {
          orderData = await adminAPI.getOrderByNumber(orderNumber);
        } else if (orderId) {
          orderData = await adminAPI.getOrder(orderId);
        } else {
          throw new Error('Не указан ID или номер заказа');
        }
        setOrder(orderData);
      } catch (error) {
        toast({
          title: "Ошибка",
          description: error.message || 'Ошибка при загрузке заказа',
          variant: "destructive",
        });
        navigate('/utech');
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderId, orderNumber, navigate, toast]);

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdating(true);
      const idToUpdate = orderId || (order ? order.id : null);
      if (!idToUpdate) {
        throw new Error('Не указан ID заказа');
      }
      await adminAPI.updateOrderStatus(idToUpdate, newStatus);
      setOrder(prev => ({ ...prev, status: newStatus }));
      toast({
        title: "Успех",
        description: "Статус заказа обновлен",
      });
    } catch (error) {
      toast({
        title: "Ошибка",
        description: error.message || 'Ошибка при обновлении статуса',
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-gray-500">Заказ не найден</p>
            <Button onClick={() => navigate('/utech')} className="w-full mt-4">
              Вернуться в панель администратора
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/utech')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Назад к панели администратора
          </Button>
          <h1 className="text-3xl font-bold">Детали заказа</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Основная информация */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Заказ №{order.order_number}</CardTitle>
                    <CardDescription>
                      Создан: {new Date(order.created_at).toLocaleString('ru-RU')}
                    </CardDescription>
                  </div>
                  <Badge variant={order.status === 'completed' ? 'default' : 'secondary'}>
                    {getStatusLabel(order.status)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Статус заказа</h3>
                    <Select
                      value={order.status}
                      onValueChange={handleStatusChange}
                      disabled={updating}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">В ожидании</SelectItem>
                        <SelectItem value="processing">В обработке</SelectItem>
                        <SelectItem value="completed">Завершен</SelectItem>
                        <SelectItem value="cancelled">Отменен</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="font-semibold mb-3">Подробный состав заказа</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-semibold">Услуга</TableHead>
                            <TableHead className="font-semibold">Конфигурация и параметры</TableHead>
                            <TableHead className="text-right font-semibold">Стоимость</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {order.items && order.items.length > 0 ? (
                            order.items.map((item, index) => {
                              const calcData = item.calculator_data || {};
                              const basePrice = calcData.basePrice || calcData.base_price || 0;
                              const userCount = calcData.userCount || calcData.user_count || calcData.quantity || 1;
                              const monthlyPrice = calcData.monthlyPrice || calcData.monthly_price || 0;
                              const oneTimePrice = calcData.oneTimePrice || calcData.one_time_price || 0;
                              const selectedServices = calcData.selectedServices || calcData.selected_services || [];
                              const totalPrice = calcData.totalPrice || calcData.total_price || (monthlyPrice + oneTimePrice);
                              
                              return (
                                <React.Fragment key={item.id}>
                                  <TableRow className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                    <TableCell className="font-medium align-top">
                                      <div className="space-y-1">
                                        <div className="font-semibold text-base">{item.product_name || 'Неизвестная услуга'}</div>
                                        {item.product_id && (
                                          <div className="text-xs text-gray-400 font-mono">ID: {item.product_id}</div>
                                        )}
                                        {item.product_price && (
                                          <div className="text-xs text-gray-500 mt-1">
                                            Базовая цена услуги: {typeof item.product_price === 'string' ? item.product_price : `${item.product_price.toLocaleString('ru-RU')} ₽`}
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="align-top">
                                      <div className="space-y-2 text-sm">
                                        {basePrice > 0 && (
                                          <div className="flex justify-between items-center py-1 border-b border-gray-100">
                                            <span className="text-gray-600">Базовая цена:</span>
                                            <span className="font-medium">{basePrice.toLocaleString('ru-RU')} ₽</span>
                                          </div>
                                        )}
                                        {userCount > 0 && (
                                          <div className="flex justify-between items-center py-1 border-b border-gray-100">
                                            <span className="text-gray-600">Количество:</span>
                                            <span className="font-medium">{userCount} {userCount === 1 ? 'пользователь' : userCount < 5 ? 'пользователя' : 'пользователей'}</span>
                                          </div>
                                        )}
                                        {selectedServices && selectedServices.length > 0 && (
                                          <div className="mt-2 pt-2 border-t border-gray-200">
                                            <div className="text-gray-700 font-medium mb-2">Дополнительные услуги:</div>
                                            <div className="space-y-1.5">
                                              {selectedServices.map((service, idx) => {
                                                const servicePrice = service.price || service.Price || 0;
                                                return (
                                                  <div key={idx} className="flex justify-between items-start py-1 px-2 bg-blue-50 rounded">
                                                    <span className="text-gray-700 text-xs flex-1">{service.name || service.Name || 'Неизвестная услуга'}</span>
                                                    <span className="text-gray-600 font-medium text-xs ml-2 whitespace-nowrap">
                                                      {servicePrice > 0
                                                        ? `+${servicePrice.toLocaleString('ru-RU')} ₽`
                                                        : (service.priceDisplay || 'по запросу')}
                                                    </span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                            {(() => {
                                              const servicesTotal = selectedServices.reduce((sum, s) => sum + (s.price || s.Price || 0), 0);
                                              return servicesTotal > 0 ? (
                                                <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-300">
                                                  <span className="text-gray-700 font-medium">Итого доп. услуги:</span>
                                                  <span className="font-semibold">{servicesTotal.toLocaleString('ru-RU')} ₽</span>
                                                </div>
                                              ) : null;
                                            })()}
                                          </div>
                                        )}
                                        {calcData.quantity !== undefined && calcData.quantity !== userCount && (
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-gray-600">Количество единиц:</span>
                                            <span className="font-medium">{calcData.quantity}</span>
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-right align-top">
                                      <div className="space-y-2">
                                        {monthlyPrice > 0 && (
                                          <div className="pb-2 border-b border-gray-200">
                                            <div className="text-xs text-gray-500 mb-1">Ежемесячная оплата:</div>
                                            <div className="font-semibold text-base text-blue-600">
                                              {monthlyPrice.toLocaleString('ru-RU')} ₽
                                            </div>
                                          </div>
                                        )}
                                        {oneTimePrice > 0 && (
                                          <div className={monthlyPrice > 0 ? "pt-2 pb-2 border-b border-gray-200" : "pb-2 border-b border-gray-200"}>
                                            <div className="text-xs text-gray-500 mb-1">Единоразовая оплата:</div>
                                            <div className="font-semibold text-base text-green-600">
                                              {oneTimePrice.toLocaleString('ru-RU')} ₽
                                            </div>
                                          </div>
                                        )}
                                        {totalPrice > 0 && (
                                          <div className="pt-2 mt-2 border-t-2 border-gray-400">
                                            <div className="text-xs text-gray-600 mb-1 font-medium">Итого по услуге:</div>
                                            <div className="font-bold text-lg text-gray-900">
                                              {totalPrice.toLocaleString('ru-RU')} ₽
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3} className="text-center text-gray-400 py-8">
                                Нет товаров в заказе
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Общая стоимость заказа */}
                    {order.items && order.items.length > 0 && (() => {
                      const totals = order.items.reduce((acc, item) => {
                        const calcData = item.calculator_data || {};
                        const monthly = calcData.monthlyPrice || calcData.monthly_price || 0;
                        const oneTime = calcData.oneTimePrice || calcData.one_time_price || 0;
                        const total = calcData.totalPrice || calcData.total_price || (monthly + oneTime);
                        return {
                          monthly: acc.monthly + monthly,
                          oneTime: acc.oneTime + oneTime,
                          total: acc.total + total,
                          count: acc.count + 1
                        };
                      }, { monthly: 0, oneTime: 0, total: 0, count: 0 });
                      
                      if (totals.total > 0 || totals.monthly > 0 || totals.oneTime > 0) {
                        return (
                          <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
                            <div className="flex justify-between items-start">
                              <div className="space-y-2 flex-1">
                                <div className="text-lg font-semibold text-gray-700 mb-3">Общая стоимость заказа</div>
                                <div className="space-y-2">
                                  {totals.count > 0 && (
                                    <div className="text-sm text-gray-600">
                                      Услуг в заказе: <span className="font-medium">{totals.count}</span>
                                    </div>
                                  )}
                                  {totals.monthly > 0 && (
                                    <div className="flex justify-between items-center py-2 px-3 bg-white rounded border border-blue-200">
                                      <span className="text-gray-700 font-medium">Ежемесячная оплата:</span>
                                      <span className="text-blue-600 font-bold text-lg">
                                        {totals.monthly.toLocaleString('ru-RU')} ₽
                                      </span>
                                    </div>
                                  )}
                                  {totals.oneTime > 0 && (
                                    <div className="flex justify-between items-center py-2 px-3 bg-white rounded border border-green-200">
                                      <span className="text-gray-700 font-medium">Единоразовая оплата:</span>
                                      <span className="text-green-600 font-bold text-lg">
                                        {totals.oneTime.toLocaleString('ru-RU')} ₽
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="ml-6 text-right">
                                <div className="text-sm text-gray-500 mb-2">Итого:</div>
                                <div className="text-3xl font-bold text-gray-900">
                                  {totals.total.toLocaleString('ru-RU')} ₽
                                </div>
                                {(totals.monthly > 0 || totals.oneTime > 0) && totals.total !== (totals.monthly + totals.oneTime) && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    (рассчитано: {(totals.monthly + totals.oneTime).toLocaleString('ru-RU')} ₽)
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Информация о клиенте */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Информация о клиенте</CardTitle>
                <CardDescription>Контактные данные и идентификация</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {order.user_email && (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Email пользователя</p>
                      <p className="font-medium break-all text-sm">{order.user_email}</p>
                    </div>
                  )}
                  {order.email && order.email !== order.user_email && (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Email для заказа</p>
                      <p className="font-medium break-all text-sm">{order.email}</p>
                    </div>
                  )}
                  {order.contact_name && (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Контактное лицо</p>
                      <p className="font-medium text-base">{order.contact_name}</p>
                    </div>
                  )}
                  {order.contact_phone && (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Телефон</p>
                      <p className="font-medium text-base">{order.contact_phone}</p>
                    </div>
                  )}
                  {order.user_id && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">ID пользователя</p>
                      <p className="font-mono text-xs break-all text-gray-600">{order.user_id}</p>
                    </div>
                  )}
                  {!order.user_email && !order.contact_name && !order.email && !order.contact_phone && (
                    <p className="text-sm text-gray-400 italic text-center py-4">Контактная информация не указана</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Организация</CardTitle>
                <CardDescription>Реквизиты и адреса</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {order.organization_name ? (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Название организации</p>
                      <p className="font-semibold text-base">{order.organization_name}</p>
                    </div>
                  ) : (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-sm text-gray-400 italic">Информация об организации не указана</p>
                    </div>
                  )}
                  {order.inn && (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">ИНН</p>
                      <p className="font-medium font-mono text-base">{order.inn}</p>
                    </div>
                  )}
                  {order.legal_address && (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Юридический адрес</p>
                      <p className="font-medium whitespace-pre-line text-sm leading-relaxed">{order.legal_address}</p>
                    </div>
                  )}
                  {order.correspondence_address && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Почтовый адрес</p>
                      <p className="font-medium whitespace-pre-line text-sm leading-relaxed">{order.correspondence_address}</p>
                    </div>
                  )}
                  {!order.organization_name && !order.inn && !order.legal_address && !order.correspondence_address && (
                    <p className="text-sm text-gray-400 italic text-center py-4">Информация об организации отсутствует</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Банковские реквизиты</CardTitle>
                <CardDescription>Платежные данные организации</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {order.bank_name ? (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Название банка</p>
                      <p className="font-semibold text-base">{order.bank_name}</p>
                    </div>
                  ) : (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-sm text-gray-400 italic">Информация о банке не указана</p>
                    </div>
                  )}
                  {order.settlement_account && (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Расчетный счет</p>
                      <p className="font-medium font-mono text-base">{order.settlement_account}</p>
                    </div>
                  )}
                  {order.correspondent_account && (
                    <div className="pb-3 border-b border-gray-200">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Корреспондентский счет</p>
                      <p className="font-medium font-mono text-base">{order.correspondent_account}</p>
                    </div>
                  )}
                  {order.bik && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">БИК</p>
                      <p className="font-medium font-mono text-base">{order.bik}</p>
                    </div>
                  )}
                  {!order.bank_name && !order.settlement_account && !order.correspondent_account && !order.bik && (
                    <p className="text-sm text-gray-400 italic text-center py-4">Банковские реквизиты не указаны</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Информация о заказе */}
            <Card>
              <CardHeader>
                <CardTitle>Информация о заказе</CardTitle>
                <CardDescription>Метаданные и статус</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="pb-3 border-b border-gray-200">
                    <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Номер заказа</p>
                    <p className="font-bold text-lg font-mono">{order.order_number}</p>
                  </div>
                  <div className="pb-3 border-b border-gray-200">
                    <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Статус</p>
                    <Badge variant={order.status === 'completed' ? 'default' : order.status === 'cancelled' ? 'destructive' : 'secondary'} className="mt-1">
                      {getStatusLabel(order.status)}
                    </Badge>
                  </div>
                  <div className="pb-3 border-b border-gray-200">
                    <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Дата создания</p>
                    <p className="font-medium text-sm">{new Date(order.created_at).toLocaleString('ru-RU', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</p>
                  </div>
                  {order.id && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">ID заказа</p>
                      <p className="font-mono text-xs break-all text-gray-600">{order.id}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
